import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "@helpers/translate";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import { updateVerification } from "@actions";
import useMediaQuery from "@mui/material/useMediaQuery";

import DoneIcon from "@assets/icons/DoneIcon";
import ArrowVerifyBackIcon from "@assets/icons/ArrowVerifyBackIcon";
import VerificationDriversLicenseIcon from "@assets/icons/VerificationDriversLicenseIcon";
import VerificationPassportIcon from "@assets/icons/VerificationPassportIcon";
import VerificationIdCardIcon from "@assets/icons/VerificationIdCardIcon";
import UploadPhotoIcon from "@assets/icons/UploadPhotoIcon";
import TakePhotoIcon from "@assets/icons/TakePhotoIcon";

import { Button } from "@components/library";
import { PROFILE_VERIFICATION_PAGE } from "@constants";

import { setDocumentType } from "@actions/verification/setDocumentType";
import { setDocumentFile } from "@actions/verification/setDocumentFile";

import {
  uploadKycDocument,
  submitKycVerification,
} from "@actions/verification/kycVerification";

import "./StepTwo.sass";

const TOTAL_STEPS = 3;

const STEP_BY_ROUTE = {
  "personal-information": 1,
  "id-verification": 2,
  result: 3,
};

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

const MAX_FILE_SIZE = 1.9 * 1024 * 1024;

const DOCUMENTS = {
  id: {
    title: "verification.step2.id-title",
    description:
      "verification.step2.id-description",
    icon: <VerificationIdCardIcon />,
  },

  license: {
    title:
      "verification.step2.license-title",
    description:
      "verification.step2.license-description",
    icon: (
      <VerificationDriversLicenseIcon />
    ),
  },

  passport: {
    title:
      "verification.step2.passport-title",
    description:
      "verification.step2.passport-description",
    icon: <VerificationPassportIcon />,
  },
};

const hasValue = (value) => {
  if (typeof value === "string") {
    return Boolean(value.trim());
  }

  return Boolean(value);
};

const FilePreview = ({
  file,
  alt,
}) => {
  const previewUrl = useMemo(() => {
    if (!file) return null;

    if (
      !(file instanceof File) &&
      !(file instanceof Blob)
    ) {
      return null;
    }

    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!previewUrl) {
    return null;
  }

  return (
    <div className="pv-verification__preview">
      <img
        src={previewUrl}
        alt={alt}
        className="pv-verification__preview-image"
      />
    </div>
  );
};

export const StepTwo = () => {
  const { __ } = useTranslation();

  const isMobileOrTablet =
    useMediaQuery("(max-width:768px)");

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const verification = useSelector(
    (state) => state.verification,
  );

  const [error, setError] = useState(null);

  const [errorField, setErrorField] =
    useState(null);

  const [submitError, setSubmitError] =
    useState(null);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [
    activeUploadField,
    setActiveUploadField,
  ] = useState(null);

  const [
    highlightUpload,
    setHighlightUpload,
  ] = useState(null);

  const [
    isDraggingField,
    setIsDraggingField,
  ] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const uploadRefs = useRef({
    front: null,
    back: null,
    selfie: null,
  });

  useEffect(() => {
    if (!verification.documentType) {
      dispatch(setDocumentType("id"));
    }
  }, [
    dispatch,
    verification.documentType,
  ]);

  const selectedDoc =
    verification.documentType || "id";

  const frontFile =
    verification.documentFiles?.front ||
    null;

  const backFile =
    verification.documentFiles?.back ||
    null;

  const selfieFile =
    verification.documentFiles?.selfie ||
    null;

  const needsBackSide =
    selectedDoc === "id" ||
    selectedDoc === "license";

  const triggerHighlight = (field) => {
    setHighlightUpload(field);

    setTimeout(() => {
      setHighlightUpload(null);
    }, 2500);
  };

  const focusUploadField = (field) => {
    setErrorField(field);

    triggerHighlight(field);

    uploadRefs.current[
      field
    ]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const validateFile = (
    selectedFile,
    field,
  ) => {
    if (!selectedFile) {
      return false;
    }

    if (
      !ALLOWED_FILE_TYPES.includes(
        selectedFile.type,
      )
    ) {
      setError(
        "verification.error.step2.file-type",
      );

      focusUploadField(field);

      return false;
    }

    if (
      selectedFile.size >
      MAX_FILE_SIZE
    ) {
      setError(
        "verification.error.step2.file-size",
      );

      focusUploadField(field);

      return false;
    }

    return true;
  };

  const validateStoredFile = (
    file,
    field,
  ) => {
    if (!file) {
      return false;
    }

    if (
      !ALLOWED_FILE_TYPES.includes(
        file.type,
      )
    ) {
      setError(
        "verification.error.step2.file-type",
      );

      focusUploadField(field);

      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(
        "verification.error.step2.file-size",
      );

      focusUploadField(field);

      return false;
    }

    return true;
  };

  const validatePersonalData = () => {
    if (
      !hasValue(
        verification.firstName,
      )
    ) {
      setSubmitError(
        __(
          "verification.error.step1.firstName",
        ),
      );

      return false;
    }

    if (
      !hasValue(
        verification.lastName,
      )
    ) {
      setSubmitError(
        __(
          "verification.error.step1.lastName",
        ),
      );

      return false;
    }

    if (
      !hasValue(
        verification.dateBirth,
      )
    ) {
      setSubmitError(
        __(
          "verification.error.step1.dateBirth",
        ),
      );

      return false;
    }

    if (
      !hasValue(
        verification.country,
      )
    ) {
      setSubmitError(
        __(
          "verification.error.step1.country",
        ),
      );

      return false;
    }

    if (
      !hasValue(
        verification.email,
      )
    ) {
      setSubmitError(
        __(
          "verification.error.step3.email",
        ),
      );

      return false;
    }

    return true;
  };

  const validateUpload = () => {
    if (!frontFile) {
      setError(
        "verification.error.step2.upload-required",
      );

      focusUploadField("front");

      return false;
    }

    if (
      !validateStoredFile(
        frontFile,
        "front",
      )
    ) {
      return false;
    }

    if (
      needsBackSide &&
      !backFile
    ) {
      setError(
        "verification.error.step2.back-required",
      );

      focusUploadField("back");

      return false;
    }

    if (
      needsBackSide &&
      !validateStoredFile(
        backFile,
        "back",
      )
    ) {
      return false;
    }

    if (!selfieFile) {
      setError(
        "verification.error.step2.selfie-required",
      );

      focusUploadField("selfie");

      return false;
    }

    if (
      !validateStoredFile(
        selfieFile,
        "selfie",
      )
    ) {
      return false;
    }

    return true;
  };

  const handleSelectDoc = (key) => {
    dispatch(setDocumentType(key));

    dispatch(
      setDocumentFile(
        "front",
        null,
      ),
    );

    dispatch(
      setDocumentFile(
        "back",
        null,
      ),
    );

    setError(null);
    setErrorField(null);
    setSubmitError(null);
    setHighlightUpload(null);
  };

  const handleFile = (
    selectedFile,
    field,
  ) => {
    if (
      !validateFile(
        selectedFile,
        field,
      )
    ) {
      return;
    }

    dispatch(
      setDocumentFile(
        field,
        selectedFile,
      ),
    );

    setError(null);
    setErrorField(null);
    setSubmitError(null);
    setHighlightUpload(null);
  };

  const handleUploadClick = (field) => {
    setActiveUploadField(field);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    fileInputRef.current?.click();
  };

  const handleTakePhoto = (field) => {
    setActiveUploadField(field);

    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }

    cameraInputRef.current?.click();
  };

  const handleRemoveFile = (field) => {
    dispatch(
      setDocumentFile(field, null),
    );

    setError(null);
    setErrorField(null);
    setSubmitError(null);
    setHighlightUpload(null);
  };

  const handleDrop = (
    event,
    field,
  ) => {
    event.preventDefault();

    setIsDraggingField(null);

    const droppedFile =
      event.dataTransfer.files?.[0];

    handleFile(
      droppedFile,
      field,
    );
  };

  const handleInputChange = (event) => {
    const selectedFile =
      event.target.files?.[0];

    if (!activeUploadField) {
      return;
    }

    handleFile(
      selectedFile,
      activeUploadField,
    );
  };

  const handleCameraInputChange = (
    event,
  ) => {
    const selectedFile =
      event.target.files?.[0];

    if (!activeUploadField) {
      return;
    }

    handleFile(
      selectedFile,
      activeUploadField,
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setSubmitError(null);

    if (!validatePersonalData()) {
      return;
    }

    if (!validateUpload()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        uploadKycDocument(
          selectedDoc,
          frontFile,
        ),
      );

      if (backFile) {
        await dispatch(
          uploadKycDocument(
            "id_back",
            backFile,
          ),
        );
      }

      await dispatch(
        uploadKycDocument(
          "selfie",
          selfieFile,
        ),
      );

      await dispatch(
        submitKycVerification(),
      );

      await dispatch(
        updateVerification(),
      );

      navigate(
        `${PROFILE_VERIFICATION_PAGE}/result`,
      );
    } catch (requestError) {
      setSubmitError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Verification failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStep = (() => {
    const match =
      location.pathname.match(
        /\/profile\/verification\/([^/?#]+)/,
      );

    return (
      STEP_BY_ROUTE[
        match?.[1]
      ] || 2
    );
  })();

  const renderUploadBlock = ({
    field,
    title,
    description,
    file,
    required = false,
  }) => (
    <div
      ref={(element) => {
        uploadRefs.current[field] =
          element;
      }}
      className={`pv-verification__content-upload ${
        isDraggingField === field
          ? "is-dragging"
          : ""
      } ${
        highlightUpload === field
          ? "pv-verification__content-upload--error"
          : ""
      }`}
      onDrop={(event) =>
        handleDrop(event, field)
      }
      onDragOver={(event) => {
        event.preventDefault();

        setIsDraggingField(field);
      }}
      onDragLeave={() =>
        setIsDraggingField(null)
      }
    >
      {file ? (
        <>
          <FilePreview
            file={file}
            alt={title}
          />

          <div className="pv-verification__content-buttons">
            <button
              type="button"
              className="pv-verification__content-button"
              onClick={() =>
                handleUploadClick(field)
              }
            >
              <UploadPhotoIcon />

              {__(
                "verification.step2.change-photo",
              )}
            </button>

            <button
              type="button"
              className="pv-verification__content-button"
              onClick={() =>
                handleRemoveFile(field)
              }
            >
              {__(
                "verification.step2.remove-photo",
              )}
            </button>
          </div>
        </>
      ) : (
        <>
          <h2 className="pv-verification__content-upload-title">
            {title}
            {required ? " *" : ""}
          </h2>

          <p className="pv-verification__content-title">
            {description}
          </p>

          <p className="pv-verification__content-title">
            {__(
              "verification.step2.upload-description.part1",
            )}{" "}
            <span
              className="pv-verification__content-browse"
              onClick={() =>
                handleUploadClick(field)
              }
            >
              {__(
                "verification.step2.upload-description.browse",
              )}
            </span>{" "}
            {__(
              "verification.step2.upload-description.part2",
            )}
          </p>

          <div className="pv-verification__content-buttons">
            <button
              type="button"
              className="pv-verification__content-button"
              onClick={() =>
                handleUploadClick(field)
              }
            >
              <UploadPhotoIcon />

              {__(
                "verification.step2.upload-photo",
              )}
            </button>

            {isMobileOrTablet && (
              <button
                type="button"
                className="pv-verification__content-button"
                onClick={() =>
                  handleTakePhoto(field)
                }
              >
                <TakePhotoIcon />

                {__(
                  "verification.step2.take-photo",
                )}
              </button>
            )}
          </div>
        </>
      )}

      {error &&
        errorField === field && (
          <p className="input-message input-message--error">
            {__(error)}
          </p>
        )}
    </div>
  );

  return (
    <div className="pv-verification">
      <div className="pv-verification__wrapper">
        <p className="pv-verification__page">
          {__("verification.step")}{" "}
          {currentStep}/{TOTAL_STEPS}
        </p>

        <h2 className="pv-verification__title">
          {__(
            "verification.step2.header",
          )}
        </h2>

        <p className="pv-verification__text">
          {__(
            "verification.step2.description",
          )}
        </p>

        <div>
          <p className="pv-verification__content-title">
            {__(
              "verification.step2.choose-title",
            )}
          </p>

          <div className="pv-verification__content-action">
            {Object.entries(
              DOCUMENTS,
            ).map(
              ([key, document]) => (
                <div
                  key={key}
                  onClick={() =>
                    handleSelectDoc(key)
                  }
                  className={`pv-verification__content-item ${
                    selectedDoc === key
                      ? "pv-verification__content-item--selected"
                      : ""
                  }`}
                >
                  <span className="pv-verification__content-icon">
                    {document.icon}
                  </span>

                  <h2 className="pv-verification__item-title">
                    {__(
                      document.title,
                    )}
                  </h2>

                  <p className="pv-verification__content-title">
                    {__(
                      document.description,
                    )}
                  </p>
                </div>
              ),
            )}
          </div>

          {renderUploadBlock({
            field: "front",
            title: `${__(
              "verification.step2.upload-title",
            )} ${__(
              DOCUMENTS[selectedDoc]
                .title,
            )}`,
            description: __(
              "verification.step2.document-front-description",
            ),
            file: frontFile,
            required: true,
          })}

          {needsBackSide &&
            renderUploadBlock({
              field: "back",
              title: __(
                "verification.step2.back-title",
              ),
              description: __(
                "verification.step2.back-description",
              ),
              file: backFile,
              required: true,
            })}

          {renderUploadBlock({
            field: "selfie",
            title: __(
              "verification.step2.selfie-title",
            ),
            description: __(
              "verification.step2.selfie-description",
            ),
            file: selfieFile,
            required: true,
          })}

          {submitError && (
            <p className="input-message input-message--error">
              {submitError}
            </p>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{
              display: "none",
            }}
            onChange={handleInputChange}
          />

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{
              display: "none",
            }}
            onChange={
              handleCameraInputChange
            }
          />
        </div>
      </div>

      <div className="pv-verification__buttons">
        <Link
          to={`${PROFILE_VERIFICATION_PAGE}/personal-information`}
          className="pv-verification__back-btn"
        >
          <ArrowVerifyBackIcon />

          {__(
            "verification.buttons.back",
          )}
        </Link>

        <Button
          color="orange"
          size="middle"
          className="pv-verification__next-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {__(
            "verification.buttons.submit",
          )}

          <DoneIcon />
        </Button>
      </div>
    </div>
  );
};

export default StepTwo;
